import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  createStyles,
  makeStyles,
  Theme,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { Add } from "@material-ui/icons";

import { toUTCTime } from "../../yoello-lib/modules/utils/TimeConversion";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import ListLoading from "../util/Loading";
import useTask from "../../hooks/useTask";
import APIExtensions from "../../yoello-lib/modules/API2/APIDefs";
import { ITradingTime } from "../../interfaces/ITradingTimes";
import { useProductsData } from "../menu/products/useProductsData";
import { TradingTimesForm } from "./TradingTimesForm";
import useCheckPermissions from "../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../const/permissions.const";
import DisabledActionWrapper from "../../yoello-lib/modules/components/DisabledActionWrapper";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    addNewButton: {
      marginBottom: "2.5rem",
      display: "flex",
      justifyContent: " end"
    },
    tradingTimes: {
      "& .tradingTime": {
        borderRadius: "10px",
        backgroundColor: "#ffffff",
        padding: "1rem",
        marginBottom: "1rem",
        "& form": {
          width: "100%"
        }
      },

      "& p": {
        display: "flex",
        flexDirection: "row-reverse",

        "& span": {
          width: "100%",
          textAlign: "right",
          padding: "0 3rem",
        },
      },

      "@media only screen and (max-width: 1280px)": {
        "& .MuiGrid-grid-xs-5": {
          width: "100%",
          maxWidth: "100%",
          flexBasis: "100%",

          "& .MuiGrid-grid-xs-1, & .MuiGrid-grid-xs-11": {
            width: "50%",
            maxWidth: "50%",
            flexBasis: "50%",
          },

          "& p": {
            "& span": {
              textAlign: "left",
              padding: "0",
            },
          },
        },
      },

      "& .active": {
        "& .MuiGrid-container": {
          display: "flex",
          flexDirection: "row-reverse",
        },
      },

    },
  })
);

export interface TradingTimesPageProps {
  venueId: string;
}

const TradingTimesPage = (props: TradingTimesPageProps) => {
  const { venueId } = props;
  const newTradingTime = useRef<null | HTMLElement>(null)
  const { increaseDoneTasks } = useTask();
  const { t } = useTranslation();
  const classes = useStyles();
  const [tradingTimes, setTradingTimes] = useState<ITradingTime[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const { callAPI, isLoading: apiLoading } = useProductsData(APIExtensions.tradingTimes, venueId, setTradingTimes, {
    "sort_field": "created_on",
    "sort_direction": "asc"
  });

  const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.VENUE_MANAGEMENT);

  useEffect(() => {
    if (isLoading) setIsLoading(false);
    // eslint-disable-next-line 
  }, [tradingTimes]);

  async function onSubmit(data: ITradingTime) {
    console.log({ data });
    const { open_from, open_to } = data;
    const method = data?.id ? HTTPMethods.PUT : HTTPMethods.POST;
    setIsLoading(true);
    await callAPI(method, {
      query: data.id,
      body: {
        ...data,
        open_from: toUTCTime(open_from),
        open_to: toUTCTime(open_to),
        exclusions: [],
        venue_id: venueId,
        name_internal: data.name_public
      },
      successCall:t("snackbar.updateSuccess"),
      successCallMethods: [HTTPMethods.PUT]
    })
    increaseDoneTasks();
  }

  const scrollLastItemIntoView = () => {
    newTradingTime?.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest"
    });
  }

  function addNewItem() {
    setTradingTimes((prevState: any) => {
      if (!prevState) {
        return [{}];
      }
      const newState = [...prevState.filter(
        (obj: ITradingTime[] | undefined) => !(obj && Object.keys(obj).length === 0 && obj.constructor === Object)
      )]
      //@ts-ignore
      newState.push({});
      return [...newState];
    })

    scrollLastItemIntoView()
  }
  const renderItems = () => {
    if (!tradingTimes || !Array.isArray(tradingTimes)) {
      return (
        <div style={{ marginBottom: "1rem", fontSize: "1rem" }}>
          {t("forms.tradingTimes.notFound")}
        </div>
      );
    }

    return tradingTimes
      .map((item: ITradingTime) => {
        return <TradingTimesForm newTradingTime={newTradingTime} formData={item as any} onSubmit={onSubmit} />
      });
  };

  return isLoading || apiLoading ? (
    <ListLoading />
  ) : (
    <>
      <div className={classes.addNewButton}>
        <DisabledActionWrapper
          disabled={!hasPermission}
          onDisabledClick={onFailedInteraction}
        >
          <Button
            onClick={addNewItem}
            size="large"
            variant="contained"
            color="primary"
            data-component-name="CREATE_NEW_DISCOUNT"
            startIcon={<Add />}
            disabled={!hasPermission}
          >
            {t("forms.tradingTimes.addNew")}
          </Button>
        </DisabledActionWrapper>
      </div>
      <div className={`${classes.tradingTimes} dialog`}>
        {renderItems()}
      </div>
    </>
  );
};

export default TradingTimesPage;
